<template>
    <div>
        <risk-header v-if="!loading" :risk="riskHeader" />

        <wizard
            no-header
            :loading="loading"
        >
            <x-form
                v-if="!loading"
                ref="productForm"
                v-model="isLinesValid"
                class="wizard-step-form"
                @submit.prevent="submit"
            >
                <x-container v-if="amendmentStep === 1" fluid>
                    <x-layout class="question-set">
                        <x-flex xs2 offset-xs1>
                            <h3 class="question-set-title">Amendment</h3>
                        </x-flex>

                        <x-flex xs6 class="question-set-questions">
                            <x-container>
                                <x-layout>
                                    <x-flex xs12>
                                        <x-radio-group
                                            v-model="declaration.risk_structure"
                                            :row="true"
                                            class="required"
                                            :rules="[
                                                v => ruleRequired(v, 'Risk Structure')
                                            ]"
                                        >
                                            <template v-slot:label>
                                                Risk Structure
                                            </template>

                                            <x-radio disabled v-for="riskStructureType in riskStructureTypes" :key="riskStructureType.value" :label="riskStructureType.name" :value="riskStructureType.value" />
                                        </x-radio-group>
                                    </x-flex>
                                </x-layout>

                                <x-layout>
                                    <x-flex xs6>
                                        <x-text-field
                                            v-model="endorsementReference"
                                            class="required"
                                            label="Endorsement reference"
                                            placeholder=" "
                                            required
                                            maxlength="10"
                                            :rules="[
                                                (v) => ruleAlphanumeric(v, 'Endorsement reference'),
                                                (v) => ruleRequired(v, 'Endorsement reference')]"
                                            outline
                                        />
                                    </x-flex>
                                </x-layout>

                                <x-layout>
                                    <x-flex xs6>
                                        <x-menu
                                            v-model="effectiveDateInput"
                                            :close-on-content-click="false"
                                            :nudge-right="40"
                                            lazy
                                            transition="scale-transition"
                                            offset-y
                                            full-width
                                            max-width="300"
                                        >
                                            <template v-slot:activator="{ on }">
                                                <x-text-field
                                                    v-model="effectiveDate"
                                                    class="required"
                                                    label="Effective date"
                                                    placeholder=" "
                                                    append-icon="event"
                                                    readonly
                                                    :rules="[v => ruleRequired(v, 'Effective date')]"
                                                    v-on="on"
                                                />
                                            </template>

                                            <x-date-picker
                                                v-model="effectiveDate"
                                                :min="$moment(declaration.inception_date).format('YYYY-MM-DD')"
                                                @input="effectiveDateInput = false"
                                            />
                                        </x-menu>
                                    </x-flex>
                                </x-layout>

                                <x-layout>
                                    <x-flex xs6>
                                        <x-menu
                                            v-model="inceptionDateInput"
                                            :close-on-content-click="false"
                                            lazy
                                            transition="scale-transition"
                                            offset-y
                                            max-width="300"
                                            full-width
                                        >
                                            <template v-slot:activator="{ on }">
                                                <x-text-field
                                                    v-model="declaration.inception_date"
                                                    class="required"
                                                    label="Inception date"
                                                    placeholder=" "
                                                    append-icon="event"
                                                    readonly
                                                    :rules="[v => ruleRequired(v, 'Inception date')]"
                                                    v-on="on"
                                                />
                                            </template>

                                            <x-date-picker
                                                v-model="declaration.inception_date"
                                                @input="inceptionDateInput = false"
                                            />
                                        </x-menu>
                                    </x-flex>

                                    <x-flex xs6>
                                        <x-menu
                                            v-model="expiryDateInput"
                                            :close-on-content-click="false"
                                            lazy
                                            transition="scale-transition"
                                            offset-y
                                            max-width="300"
                                            full-width
                                        >
                                            <template v-slot:activator="{ on }">
                                                <x-text-field
                                                    v-model="declaration.expiry_date"
                                                    class="required"
                                                    label="Expiry date"
                                                    placeholder=" "
                                                    append-icon="event"
                                                    readonly
                                                    :rules="[v => ruleRequired(v, 'Expiry date')]"
                                                    v-on="on"
                                                />
                                            </template>

                                            <x-date-picker
                                                v-model="declaration.expiry_date"
                                                :min="$moment().format('YYYY-MM-DD')"
                                                @input="expiryDateInput = false"
                                            />
                                        </x-menu>
                                    </x-flex>
                                </x-layout>

                                <x-layout>
                                    <x-flex xs12>
                                        <text-editor
                                            v-model="commentary"
                                            key="commentary-text-editor"
                                            label="Commentary"
                                            required
                                            :rules="[v => ruleRequired(v, 'Commentary')]"
                                        />
                                    </x-flex>
                                </x-layout>
                            </x-container>
                        </x-flex>
                    </x-layout>

                    <standalone-policy-questions
                        v-if="isStandalonePolicy"
                        :submission="declaration"
                        :currencies="currencies"
                        :countries="countries"
                        :choices_of_law="choices_of_law"
                        :categories="categories"
                        :products="products"
                        :is-amendment="true"
                    />

                    <editable-generic-questions
                        v-else-if="!isInvoice"
                        v-model="declaration"
                        :currencies="currencies"
                    />

                    <x-layout class="action-set" align-center>
                        <x-flex xs6 offset-xs3 class="action-set-actions">
                            <x-container>
                                <x-layout>
                                    <x-flex xs6 v-if="amendmentStep === 1">
                                        <x-btn
                                            color="tertiary"
                                            @click="goToDeclaration"
                                        >
                                            <i class="material-icons mr-2">arrow_back</i>

                                            Exit Amendment
                                        </x-btn>
                                    </x-flex>
                                    <x-flex xs6 v-else>
                                        <x-btn
                                            color="tertiary"
                                            @click="amendmentStep--"
                                        >
                                            <i class="material-icons mr-2">arrow_back</i>

                                            Back
                                        </x-btn>
                                    </x-flex>

                                    <x-flex xs6 text-xs-right>
                                        <x-btn
                                            class="wide"
                                            color="primary"
                                            @click="saveChanges"
                                            :loading="btnLoading"
                                        >
                                            Save changes

                                            <i class="material-icons ml-2">trending_flat</i>
                                        </x-btn>
                                    </x-flex>

                                    <x-flex xs6 text-xs-right>
                                        <x-btn
                                            class="wide"
                                            color="primary"
                                            @click="nextStep"
                                        >
                                            Next

                                            <i class="material-icons ml-2">trending_flat</i>
                                        </x-btn>
                                    </x-flex>
                                </x-layout>
                            </x-container>
                        </x-flex>
                    </x-layout>
                </x-container>

                <template v-if="amendmentStep === 2">
                    <product-questions
                        v-model="declaration"
                        :facilities="facilities"
                        :isAmendment="true"
                    >
                        <template v-slot:submit-row>
                            <x-layout class="action-set" align-center>
                                <x-flex xs6 offset-xs3 class="action-set-actions">
                                    <x-container>
                                        <x-layout>
                                            <x-flex xs6>
                                                <x-btn
                                                    color="tertiary"
                                                    @click="amendmentStep = 1"
                                                >
                                                    <i class="material-icons mr-2">arrow_back</i>

                                                    Back
                                                </x-btn>
                                            </x-flex>

                                            <x-flex xs6 text-xs-right>
                                                <x-btn
                                                    class="wide"
                                                    color="primary"
                                                    @click="saveChanges"
                                                    :loading="btnLoading"
                                                >
                                                    Save changes

                                                    <i class="material-icons ml-2">trending_flat</i>
                                                </x-btn>
                                            </x-flex>

                                            <x-flex xs6 text-xs-right>
                                                <x-btn
                                                    class="wide"
                                                    color="primary"
                                                    @click="nextStep"
                                                >
                                                    Next

                                                    <x-icon class="ml-2">trending_flat</x-icon>
                                                </x-btn>
                                            </x-flex>
                                        </x-layout>
                                    </x-container>
                                </x-flex>
                            </x-layout>
                        </template>
                    </product-questions>
                </template>

                <x-container v-if="amendmentStep === 3" fluid>
                    <x-layout mx-2>
                        <x-flex xs12 class="right">
                            <x-card v-if="isCaptiveReinsurance" class="panel mb-4">
                                <x-card-title>
                                    <h3>Captive line</h3>
                                </x-card-title>

                                <x-card-text>
                                    <x-data-table
                                        class="captive-line-table no-row-hover double-rows"
                                        :headers="captiveLineTableHeaders"
                                        :items="[captiveDeclarationLine]"
                                        hide-actions
                                    >
                                        <template v-slot:no-data>
                                            <p class="error-message text-xs-center">No lines found</p>
                                        </template>

                                        <template v-slot:items="props">
                                            <tr>
                                                <td width="20%">
                                                    {{ getInsurerCompanyName(props.item) }}
                                                </td>

                                                <td width="15%">
                                                    <x-text-field
                                                        v-model="props.item.brokerage"
                                                        class="required input-text-right input-no-margin"
                                                        suffix="%"
                                                        hide-details
                                                        :rules="[
                                                            v => ruleRequired(v, 'Brokerage'),
                                                            v => ruleGreaterThanOrEqualTo(v, 0, 'Brokerage'),
                                                            v => ruleLessThanOrEqualTo(v, 100, 'Brokerage')
                                                        ]"
                                                        @keypress.enter.prevent
                                                    />
                                                </td>

                                                <td width="15%">
                                                    {{ props.item.price }} BPS

                                                    <div v-if="showHorizontalPriceChange(0) && pricing.horizontalPrice !== props.item.price">
                                                        <x-icon size="18">arrow_upward</x-icon>

                                                        {{ pricing.horizontalPrice }} BPS
                                                    </div>

                                                    <div v-else-if="showHorizontalPercentageChange(null, true) && pricing.horizontalPrice !== props.item.price">
                                                        <x-icon size="18">arrow_upward</x-icon>

                                                        {{ pricing.horizontalPrice }} BPS
                                                    </div>
                                                </td>

                                                <td width="50%">
                                                    <value-percent-split-input
                                                        v-model="props.item.line_percent"
                                                        :amount-value.sync="props.item.line_amount"
                                                        :total="lineRequired"
                                                        :currency-code="currencyCode"
                                                        required
                                                        :maxAmount="captiveMaxRetainedAmount"
                                                        :allowZero="true"
                                                        hide-details
                                                        no-margin
                                                        :percentage-rules="[v => ruleLessThanOrEqualTo(v, 50, 'Percentage')]"
                                                    />
                                                </td>
                                            </tr>

                                            <tr v-show="getInsurerSecurities(props.item).length > 0">
                                                <td colspan="4">
                                                    <x-select
                                                        v-model="props.item.security_id"
                                                        :items="getSecuritiesFormatted(getInsurerSecurities(props.item))"
                                                        label="Securities"
                                                        placeholder="Security"
                                                        append-icon="keyboard_arrow_down"
                                                        hide-details
                                                        :menu-props="{
                                                            offsetY: true,
                                                            contentClass: 'input-fixed-width-menu'
                                                        }"
                                                    >
                                                        <template slot="selection" slot-scope="{item, index}">
                                                            <span :title="item.text">{{ item.text }}</span>
                                                        </template>
                                                    </x-select>
                                                </td>
                                            </tr>
                                        </template>
                                    </x-data-table>
                                </x-card-text>
                            </x-card>

                            <x-card class="panel mb-4">
                                <x-card-title>
                                    <h3>Signed lines</h3>
                                </x-card-title>

                                <x-card-text>
                                    <x-data-table
                                        class="no-row-hover double-rows"
                                        :headers="linesTableHeaders"
                                        :items="nonCaptiveDeclarationLines"
                                        hide-actions
                                    >
                                        <template v-slot:no-data>
                                            <p class="error-message text-xs-center">No lines found</p>
                                        </template>

                                        <template v-slot:items="props">
                                            <tr>
                                                <td>
                                                    {{ getInsurerCompanyName(props.item) }}
                                                </td>

                                                <td>
                                                    <x-text-field
                                                        v-if="!isCaptiveReinsurance"
                                                        v-model="props.item.brokerage"
                                                        class="required input-text-right input-no-margin"
                                                        suffix="%"
                                                        hide-details
                                                        :rules="[
                                                            v => ruleRequired(v, 'Brokerage'),
                                                            v => ruleGreaterThanOrEqualTo(v, 0, 'Brokerage'),
                                                            v => ruleLessThanOrEqualTo(v, 100, 'Brokerage')
                                                        ]"
                                                        @keypress.enter.prevent
                                                    />

                                                    <span v-else>{{ props.item.brokerage | percent }}</span>
                                                </td>

                                                <td>
                                                    <x-select
                                                        v-model="props.item.insurer_role"
                                                        :items="insurerRoleOptions"
                                                        placeholder="Optional"
                                                        clearable
                                                        hide-details
                                                        append-icon="keyboard_arrow_down"
                                                        :menu-props="{
                                                            offsetY: true,
                                                            contentClass: 'input-fixed-width-menu',
                                                        }"
                                                        @change="handleInsurerRoleChange(props.item, $event)"
                                                    />
                                                </td>

                                                <td>
                                                    <x-select
                                                        v-model="props.item.offer_type"
                                                        :items="responseTypes"
                                                        class="input-no-margin"
                                                        placeholder="Select line type"
                                                        item-text="text"
                                                        item-value="value"
                                                        append-icon="keyboard_arrow_down"
                                                        hide-details
                                                        @change="handleOfferTypeChange(props.item)"
                                                        :menu-props="{
                                                            offsetY: true,
                                                            maxHeight: 'none',
                                                            value: false,
                                                            openOnClick: true,
                                                            closeOnClick: true,
                                                            closeOnContentClick: true,
                                                            transition: 'scroll-y-transition'
                                                        }"
                                                        :rules="[v => ruleRequired(v, 'Line type')]"
                                                    />
                                                </td>

                                                <td>
                                                    <template v-if="props.item.offer_type === 'rate'">
                                                        <x-text-field
                                                            v-model="props.item.price"
                                                            class="required input-small input-no-margin"
                                                            placeholder="Price"
                                                            suffix="BPS"
                                                            hide-details
                                                            :rules="[ruleRequired, ruleNumeric]"
                                                        />

                                                        <div v-if="showHorizontalPriceChange(props.item.price)">
                                                            <x-icon size="18">arrow_upward</x-icon>

                                                            {{ pricing.horizontalPrice }} BPS
                                                        </div>
                                                    </template>

                                                    <template v-else-if="['percentage_of_net_margin', 'percentage_of_gross_margin'].includes(props.item.offer_type)">
                                                        <div class="d-flex child-flex">
                                                            <x-currency-text-field
                                                                v-model="props.item.price"
                                                                class="required value-percent-amount input-no-margin"
                                                                suffix="BPS"
                                                                hide-details
                                                                :rules="[
                                                                    () => ruleRequired(props.item.price, 'Price'),
                                                                    () => ruleGreaterThanOrEqualTo(props.item.price, (props.item.line_percent ? 1 : 0), 'Price')
                                                                ]"
                                                            />

                                                            <span class="value-percent-divider no-margin">/</span>

                                                            <x-text-field
                                                                v-model="props.item.percentage_of_margin"
                                                                class="value-percent-percentage input-text-right input-no-margin"
                                                                suffix="%"
                                                                hide-details
                                                                :rules="[
                                                                    v => ruleRequired(v, 'Percentage'),
                                                                    v => props.item.line_percent ? ruleGreaterThan(v, 0, 'Percentage') : ruleGreaterThanOrEqualTo(v, 0, 'Percentage')
                                                                ]"
                                                            />

                                                            <!-- THIS WILL BE REACTIVATED IN FUTURE FOR CAPTIVE REINSURANCE -->
                                                            <div v-if="!isCaptiveReinsurance && showHorizontalPercentageChange(props.item)">
                                                                <x-icon size="18">arrow_upward</x-icon>

                                                                {{ pricing.horizontalPrice | numberNoTrail }} BPS
                                                            </div>
                                                        </div>
                                                    </template>

                                                    <template v-else-if="props.item.offer_type === 'flat_fee'">
                                                        <x-currency-text-field
                                                            v-model="props.item.flat_fee"
                                                            class="required input-no-margin"
                                                            placeholder="Flat fee"
                                                            :prefix="currencyCode"
                                                            hide-details
                                                            hide-helper
                                                            :rules="[
                                                                ruleRequired,
                                                                v => ruleNumeric(v, false, false, true)
                                                            ]"
                                                        />
                                                    </template>
                                                </td>

                                                <td>
                                                    <value-percent-split-input
                                                        v-model="props.item.line_percent"
                                                        :amount-value.sync="props.item.line_amount"
                                                        :total="lineRequired"
                                                        :currency-code="currencyCode"
                                                        :allowZero="true"
                                                        required
                                                        hide-details
                                                        no-margin
                                                    />
                                                </td>

                                                <td class="text-xs-center">
                                                    <x-btn
                                                        color="tertiary"
                                                        class="square-icon"
                                                        small
                                                        title="Click to remove the insurer's participation from declaration"
                                                        @click="clearInsurerData(props.item)"
                                                    >
                                                        <i class="material-icons">close</i>
                                                    </x-btn>
                                                </td>
                                            </tr>

                                            <tr v-show="getInsurerSecurities(props.item).length > 0">
                                                <td :colspan="linesTableHeaders.length">
                                                    <x-select
                                                        v-model="props.item.security_id"
                                                        :items="getSecuritiesFormatted(getAvailableSecurities(props.item))"
                                                        label="Securities"
                                                        placeholder="Security"
                                                        append-icon="keyboard_arrow_down"
                                                        hide-details
                                                        :menu-props="{
                                                            offsetY: true,
                                                            contentClass: 'input-fixed-width-menu',
                                                        }"
                                                    >
                                                        <template slot="selection" slot-scope="{item, index}">
                                                            <span :title="item.text">{{ item.text }}</span>
                                                        </template>
                                                    </x-select>
                                                </td>
                                            </tr>
                                        </template>
                                    </x-data-table>

                                    <x-data-table
                                        v-if="newLines.length > 0"
                                        class="no-row-hover double-rows mt-4"
                                        :headers="newLinesTableHeaders"
                                        :items="newLines"
                                        hide-actions
                                    >
                                        <template v-slot:no-data>
                                            <p class="error-message text-xs-center">No lines found</p>
                                        </template>

                                        <template v-slot:items="props">
                                            <tr>
                                                <td>
                                                    <x-select
                                                        v-model="props.item.insurer_company_id"
                                                        :items="getAvailableInsurers(props.item)"
                                                        class="required input-no-margin"
                                                        placeholder="Select insurer"
                                                        item-text="name"
                                                        item-value="id"
                                                        append-icon="keyboard_arrow_down"
                                                        hide-details
                                                        :menu-props="{
                                                            offsetY: true,
                                                            maxHeight: '400',
                                                            value: false,
                                                            openOnClick: true,
                                                            closeOnClick: true,
                                                            closeOnContentClick: true,
                                                            transition: 'scroll-y-transition'
                                                        }"
                                                        :rules="[
                                                            v => ruleRequired(v, 'Insurer')
                                                        ]"
                                                    />
                                                </td>

                                                <td>
                                                    <x-text-field
                                                        v-model="props.item.brokerage"
                                                        class="required input-text-right input-no-margin"
                                                        suffix="%"
                                                        hide-details
                                                        :rules="[
                                                            v => ruleRequired(v, 'Brokerage'),
                                                            v => ruleGreaterThanOrEqualTo(v, 0, 'Brokerage'),
                                                            v => ruleLessThanOrEqualTo(v, 100, 'Brokerage')
                                                        ]"
                                                        @keypress.enter.prevent
                                                    />
                                                </td>

                                                <td>
                                                    <x-select
                                                        v-model="props.item.insurer_role"
                                                        :items="insurerRoleOptions"
                                                        placeholder="Optional"
                                                        clearable
                                                        hide-details
                                                        append-icon="keyboard_arrow_down"
                                                        :menu-props="{
                                                            offsetY: true,
                                                            contentClass: 'input-fixed-width-menu',
                                                        }"
                                                        @change="handleInsurerRoleChange(props.item, $event)"
                                                    />
                                                </td>

                                                <td>
                                                    <x-select
                                                        v-model="props.item.offer_type"
                                                        :items="responseTypes"
                                                        class="required input-no-margin"
                                                        append-icon="keyboard_arrow_down"
                                                        placeholder="Select line type"
                                                        item-text="text"
                                                        item-value="value"
                                                        hide-details
                                                        :menu-props="{
                                                            offsetY: true,
                                                            maxHeight: 'none',
                                                            value: false,
                                                            openOnClick: true,
                                                            closeOnClick: true,
                                                            closeOnContentClick: true,
                                                            transition: 'scroll-y-transition'
                                                        }"
                                                        :rules="[
                                                            v => ruleRequired(v, 'Line type')
                                                        ]"
                                                    />
                                                </td>

                                                <td v-if="props.item.offer_type === 'rate'">
                                                    <x-text-field
                                                        v-model="props.item.price"
                                                        class="required input-no-margin"
                                                        suffix="BPS"
                                                        placeholder="Price"
                                                        hide-details
                                                        :rules="[ruleRequired, ruleNumeric]"
                                                    />
                                                </td>

                                                <td v-else-if="['percentage_of_net_margin', 'percentage_of_gross_margin'].includes(props.item.offer_type)">
                                                    <div class="d-flex child-flex">
                                                        <x-currency-text-field
                                                            v-model="props.item.price"
                                                            class="required value-percent-amount input-no-margin"
                                                            suffix="BPS"
                                                            hide-details
                                                            :rules="[
                                                                () => ruleRequired(props.item.price, 'Price'),
                                                                () => ruleGreaterThanOrEqualTo(props.item.price, 1, 'Price')
                                                            ]"
                                                        />

                                                        <span class="value-percent-divider no-margin">/</span>

                                                        <x-text-field
                                                            v-model="props.item.percentage_of_margin"
                                                            class="value-percent-percentage input-text-right input-no-margin"
                                                            suffix="%"
                                                            hide-details
                                                            :rules="[
                                                                v => ruleRequired(v, 'Percentage'),
                                                                v => ruleGreaterThan(v, 0, 'Percentage')
                                                            ]"
                                                        />
                                                    </div>
                                                </td>

                                                <td v-else-if="props.item.offer_type === 'flat_fee'">
                                                    <x-currency-text-field
                                                        v-model="props.item.flat_fee"
                                                        class="required input-no-margin"
                                                        placeholder="Flat fee"
                                                        :prefix="currencyCode"
                                                        hide-details
                                                        hide-helper
                                                        :rules="[
                                                            ruleRequired,
                                                            v => ruleNumeric(v, false, false, true)
                                                        ]"
                                                    />
                                                </td>

                                                <td v-else>
                                                    Select line type
                                                </td>

                                                <td>
                                                    <value-percent-split-input
                                                        v-model="props.item.line_percent"
                                                        :amount-value.sync="props.item.line_amount"
                                                        :total="lineRequired"
                                                        :currency-code="currencyCode"
                                                        required
                                                        hide-details
                                                        no-margin
                                                    />
                                                </td>

                                                <td class="text-xs-center">
                                                    <x-btn
                                                        color="tertiary"
                                                        class="square-icon"
                                                        small
                                                        title="Remove"
                                                        @click="removeInsurer(props.index)"
                                                    >
                                                        <i class="material-icons">close</i>
                                                    </x-btn>
                                                </td>
                                            </tr>

                                            <tr v-show="props.item.insurer_company_id && getInsurerSecurities(props.item).length > 0">
                                                <td v-if="props.item.insurer_company_id && getInsurerSecurities(props.item).length > 0" colspan="5">
                                                    <x-select
                                                        v-model="props.item.security_id"
                                                        :items="getSecuritiesFormatted(getAvailableSecurities(props.item))"
                                                        class="required"
                                                        label="Securities"
                                                        placeholder="Security"
                                                        append-icon="keyboard_arrow_down"
                                                        :menu-props="{
                                                            offsetY: true,
                                                            contentClass: 'input-fixed-width-menu',
                                                        }"
                                                        :rules="[v => ruleRequired(v, 'Security', 'Select a security for this insurer')]"
                                                    >
                                                        <template slot="selection" slot-scope="{item, index}">
                                                            <span :title="item.text">{{ item.text }}</span>
                                                        </template>
                                                    </x-select>
                                                </td>
                                            </tr>
                                        </template>
                                    </x-data-table>

                                    <x-container fluid pa-0>
                                        <x-layout mt-4>
                                            <x-flex xs4>
                                                <x-text-field
                                                    v-if="netMarginRequired"
                                                    v-model="customNetMargin"
                                                    class="required"
                                                    label="Net margin"
                                                    suffix="BPS"
                                                    :rules="[
                                                        v => ruleRequired(v, 'Net margin'),
                                                        v => ruleNumeric(v, 'Net margin'),
                                                        v => ruleGreaterThanOrEqualTo(v, 0, 'Net margin')
                                                    ]"
                                                    outline
                                                />
                                                <x-text-field
                                                    v-if="grossMarginRequired"
                                                    v-model="customGrossMargin"
                                                    class="required"
                                                    label="Gross margin"
                                                    suffix="BPS"
                                                    :rules="[
                                                        v => ruleRequired(v, 'Gross margin'),
                                                        v => ruleNumeric(v, 'Gross margin'),
                                                        v => ruleGreaterThanOrEqualTo(v, 0, 'Gross margin')
                                                    ]"
                                                />
                                            </x-flex>
                                        </x-layout>

                                        <x-layout mt-5>
                                            <x-flex xs12 text-xs-center>
                                                <x-btn
                                                    color="secondary"
                                                    @click="addInsurer()"
                                                >
                                                    <i class="material-icons mr-2">add</i>
                                                    Add {{ insurerType }}
                                                </x-btn>
                                            </x-flex>
                                        </x-layout>

                                    </x-container>
                                </x-card-text>
                            </x-card>
                        </x-flex>
                    </x-layout>

                    <x-layout v-if="isCaptiveReinsurance" mx-2 mb-5>
                        <x-flex xs4 class="left">
                            <x-card class="panel">
                                <x-card-text>
                                    <data-list v-model="captiveLinesDataList" />
                                </x-card-text>
                            </x-card>
                        </x-flex>

                        <x-flex xs8>
                            <x-card class="panel">
                                <x-card-text>
                                    <progress-linear-bar
                                        v-model="captivePercentageWritten"
                                        class="mb-4"
                                        label="Captive"
                                        end-label="Reinsured"
                                        :bar-label="null"
                                        bar-label-class="grey--text text--lighten-2"
                                        color="teal"
                                        background-color="teal"
                                        :background-opacity="0.3"
                                    />

                                    <progress-linear-bar
                                        v-model="percentageWritten"
                                        class="mb-4"
                                        label="Written"
                                        :end-label="$options.filters.percent(percentageWritten)"
                                        :end-label-color="getEndLabelColor(percentageWritten)"
                                        color="teal"
                                    />
                                </x-card-text>
                            </x-card>
                        </x-flex>
                    </x-layout>

                    <x-layout v-else mx-2 mb-5>
                        <x-flex xs4 class="left">
                            <x-card class="panel">
                                <x-card-text>
                                    <data-list v-model="linesDataList" />
                                </x-card-text>
                            </x-card>
                        </x-flex>

                        <x-flex xs8>
                            <x-card class="panel">
                                <x-card-text>
                                    <div class="d-inline-flex mb-4">
                                        <x-switch
                                            v-model="pricingStructure"
                                            class="double-switch ma-0 pa-0"
                                            label="Horizontal"
                                            true-value="vertical"
                                            false-value="horizontal"
                                            color="primary"
                                            hide-details
                                        >
                                            <template v-slot:append>Vertical</template>
                                        </x-switch>

                                        <x-icon v-if="this.pricingStructure !== this.declaration.pricing_structure" color="yellow" class="ml-4" :size="22">warning</x-icon>
                                    </div>

                                    <progress-linear-bar
                                        v-model="percentageWritten"
                                        class="mb-4"
                                        label="Written"
                                        :end-label="$options.filters.percent(percentageWritten)"
                                        :end-label-color="getEndLabelColor(percentageWritten)"
                                        color="teal"
                                    />
                                </x-card-text>
                            </x-card>
                        </x-flex>
                    </x-layout>

                    <x-layout v-if="!isStandalonePolicy" class="question-set">
                        <x-flex xs2 offset-xs1>
                            <h3 class="question-set-title">Additional Information <span v-if="!this.declaration.product_data.prior_experience" class="question-set-subtitle">(optional)</span></h3>
                        </x-flex>

                        <x-flex xs6 class="question-set-questions">
                            <additional-information-edit
                                v-model="declaration.information_sections"
                                :insurer-list="insurerList"
                            />
                        </x-flex>
                    </x-layout>

                    <x-layout mx-2 mb-5>
                        <x-flex>
                            <document-list
                                v-if="documentableId"
                                :insurer-list="insurerList"
                                insurer-specific-upload
                                upload
                                :allowPolicyWordingAndMRCHandling="isStandalonePolicy"
                                :documentable-id="documentableId"
                                :facility-id="declaration.facility_id"
                                documentable-type="endorsement"
                                @documentChanges="documentChanges($event)"
                                @documentsLoading="documentsLoading = $event"
                                @update-document-categories="handleDocumentCategories"
                            />
                        </x-flex>
                    </x-layout>

                    <x-layout class="action-set action-set-wide" align-center>
                        <x-flex xs12 class="action-set-actions">
                            <x-container fluid>
                                <x-layout>
                                    <x-flex xs6>
                                        <x-btn
                                            color="tertiary"
                                            @click="amendmentStep = 2"
                                        >
                                            <i class="material-icons mr-2">arrow_back</i>

                                            Back
                                        </x-btn>
                                    </x-flex>

                                    <x-flex xs6 text-xs-right>
                                        <x-btn
                                            class="wide"
                                            color="primary"
                                            @click="saveChanges"
                                            :loading="btnLoading"
                                        >
                                            Save changes

                                            <i class="material-icons ml-2">trending_flat</i>
                                        </x-btn>
                                    </x-flex>

                                    <x-flex xs6 text-xs-right>
                                        <x-btn
                                            class="wide"
                                            color="primary"
                                            type="submit"
                                            :disabled="submitButtonDisabled"
                                        >
                                            Submit

                                            <x-icon class="ml-2">trending_flat</x-icon>
                                        </x-btn>
                                    </x-flex>
                                </x-layout>
                            </x-container>
                        </x-flex>
                    </x-layout>
                </x-container>
            </x-form>

            <modal
                ref="confirmAmendment"
                title="Confirm amendment"
                confirmation
                :action-disabled="this.approvingInsurerIds.length === 0"
            >
                <x-layout>
                    <div class="text-xs-left">
                        <p>You are about to make a change to an enquiry that is bound.</p>

                        <p>This will submit an amendment request to the underwriters bound against this declaration.</p>

                        <p>Are you sure?</p>
                    </div>
                </x-layout>

                <x-layout justify-center>
                    <x-flex xs6>
                        <x-select
                            label="Require approval from"
                            :items="approvalList"
                            v-model="approvingInsurerIds"
                            placeholder="Require approval from"
                            append-icon="keyboard_arrow_down"
                            outline
                            item-text="name"
                            item-value="id"
                            :item-disabled="({is_new_insurer}) => is_new_insurer"
                            multiple
                            hide-details
                            data-test="approvers"
                            :menu-props="{
                        offsetY: true,
                        maxHeight: 400
                    }"
                        >
                            <template slot="selection" slot-scope="{item, index}">
                                <span v-if="index === 0">{{ numberOfInsurersSelected }}</span>
                            </template>

                            <template slot="prepend-item">
                                <x-list-tile class="buttons-top-tile no-hover">
                                    <x-btn @click="selectAllInsurers" color="secondary">Select all</x-btn>
                                    <x-btn data-test="deselect_all" @click="deselectAllInsurers" color="tertiary">Deselect all</x-btn>
                                </x-list-tile>
                            </template>
                        </x-select>
                    </x-flex>
                </x-layout>
            </modal>
            <modal
                ref="noEndorsementDocument"
                title="Endorsement document"
                confirmation
            >
                <div class="text-xs-left">
                    <p>You have not uploaded an Endorsement Document.</p>

                    <p>Are you sure that you wish to proceed?</p>
                </div>
            </modal>
            <modal
                ref="removeInsurer"
                title="Remove insurer"
                confirmation
            >
                <div class="text-xs-left">
                    Do you wish to proceed to remove the insurer's participation from the declaration?
                </div>
            </modal>

            <modal ref="notUniqueEndorsementReference" title="Error">
                <p>Endorsement reference not unique.</p>
                <p>Please change it.</p>
            </modal>
        </wizard>
    </div>
</template>

<script>
import RiskHeader from '~/components/Shared/Partials/RiskHeader';
import ValuePercentSplitInput from "~/components/Shared/UIComponents/ValuePercentSplitInput";
import ProductQuestions from '~/components/Broker/Products/ProductQuestions';
import TextEditor from '~/components/Shared/UIComponents/Editor/TextEditor';
import AdditionalInformationEdit from '~/components/Broker/UIComponents/AdditionalInformationEdit';
import rules from '~/mixins/rules';
import DocumentList from '~/components/Shared/UIComponents/DocumentList';
import Modal from "~/components/Shared/UIComponents/Modal";
import ProgressLinearBar from '~/components/Broker/UIComponents/ProgressLinearBar';
import DataList from '~/components/Shared/UIComponents/DataList';
import EditableGenericQuestions from '~/components/Broker/SubmissionWizard/EditableGenericQuestions.vue';
import StandalonePolicyQuestions from '~/components/Broker/SubmissionWizard/StandalonePolicyQuestions.vue';
import Big from 'big.js'
import Wizard from '../../components/Broker/UIComponents/Wizard.vue';
import insurerRoleOptions from '~/mixins/insurerRoleOptions';

export default {
    name: 'BrokerAmendmentEntry',
    mixins: [rules, insurerRoleOptions],
    components: {
        Wizard,
        Modal,
        RiskHeader,
        ValuePercentSplitInput,
        ProductQuestions,
        TextEditor,
        AdditionalInformationEdit,
        DocumentList,
        ProgressLinearBar,
        DataList,
        EditableGenericQuestions,
        StandalonePolicyQuestions
    },
    data() {
        return {
            declaration: {currency: {}},
            oldDeclaration: {},
            facilities: [],
            currencies: [],
            amendment: [],
            amendmentId: null,
            amendmentStep: 1,
            loading: true,
            declarationHasChanged: false,
            declarationLinesChanged: false,
            effectiveDateChanged: false,
            isEditDisabled: true,
            effectiveDate: null,
            effectiveDateInput: false,
            inceptionDateInput: false,
            expiryDateInput: false,
            commentary: null,
            newLines: [],
            insurers: [],
            pricingStructure: null,
            isLinesValid: false,
            customNetMargin: null,
            customGrossMargin: null,
            originalNetMargin: null,
            originalGrossMargin: null,
            countries: [],
            choices_of_law: [],
            categories: [],
            products: [],
            approvingInsurerIds: [],
            linesTableHeaders: [
                {
                    text: 'Insurer',
                    value: 'insurer_company.name'
                },
                {
                    text: 'Brokerage',
                    value: 'brokerage'
                },
                {
                    text: 'Insurer role',
                    value: 'insurer_role'
                },
                {
                    text: 'Line type',
                    value: 'type',
                    hide_for_captive_line: true
                },
                {
                    text: 'Price',
                    value: 'price'
                },
                {
                    text: 'Written line',
                    value: 'line_percent'
                },
                {
                    text: 'Remove',
                    sortable: false,
                    align: 'center',
                },
            ],
            newLinesTableHeaders: [
                {
                    text: 'Insurer',
                    value: 'insurer_company_id',
                    sortable: false
                },
                {
                    text: 'Brokerage',
                    value: 'brokerage',
                    sortable: false
                },
                {
                    text: 'Insurer role',
                    value: 'insurer_role',
                    sortable: false
                },
                {
                    text: 'Line type',
                    value: 'line_type',
                    sortable: false
                },
                {
                    text: 'Price',
                    value: 'price',
                    sortable: false
                },
                {
                    text: 'Written line',
                    value: 'line_percent',
                    sortable: false
                },
                {
                    text: 'Remove',
                    sortable: false,
                    align: 'center',
                },
            ],
            percentageDecimalPlaces: 12,
            pricing: {
                horizontalPrice: 0,
                horizontalPercentageOfMargin: 0,
            },
            riskStructureTypes: [
                {
                    value: 'accepted_declaration',
                    name: 'Accepted Declaration'
                },
                {
                    value: 'standalone_policy',
                    name: 'Standalone Policy'
                }
            ],
            btnLoading: false,
            documentsLoading: false,
            endorsementDocumentWarningDisplayed: false,
            documentCategoriesFromChild: [],
            endorsementReference: '',
            endorsementReferenceExists: false,
            securities: [],
        }
    },
    computed: {
        currencyCode() {
            const selectedCurrency = this.currencies.find(currency => this.declaration.currency_id === currency.id) || {};
            return selectedCurrency.code || '';
        },
        insurerType() {
            if (this.isCaptiveReinsurance) {
                return 'reinsurer';
            }

            return 'insurer'
        },
        submitButtonDisabled() {
            return !this.isLinesValid || this.isEditDisabled || !this.isLineWrittenFull || !this.hasValidDeclarationLines || this.marginNotProvided || this.btnLoading;
        },
        marginNotProvided() {
            return ((this.netMarginRequired && !this.isLinesValid) || (this.grossMarginRequired && !this.isLinesValid));
        },
        netMarginRequired() {
            return !this.originalNetMargin && this.hasPercentageOfNetMarginLines;
        },
        grossMarginRequired() {
            return !this.originalGrossMargin && this.hasPercentageOfGrossMarginLines;
        },
        captiveLineTableHeaders() {
            return this.linesTableHeaders.filter(type => !type.hide_for_captive_line);
        },
        productComponent: function () {
            return this.$options.filters.snakeToPascal(this.declaration.product_config_key);
        },
        riskHeader() {
            return {
                id: this.declaration.risk_id,
                client_company_name: this.declaration.client_company?.name,
                obligor_name: this.declaration.obligor_name,
                country_name: this.declaration.country_id ? this.declaration.country.name : '',
                status: this.declaration.status
            };
        },
        nonCaptiveDeclarationLines() {
            return this.isCaptiveReinsurance
                ? this.declaration?.lines.filter((line) => !line.captive_insurer_company_id) || [] : this.declaration?.lines || [];
        },
        captiveDeclarationLine() {
            return this.declaration.lines.find((line) => Boolean(line.captive_insurer_company_id));
        },
        declarationLines() {
            return [...this.nonCaptiveDeclarationLines, ...this.newLines];
        },
        allDeclarationLines() {
            return [...this.declaration.lines, ...this.newLines];
        },
        captiveLinesDataList() {
            return [
                {
                    text: 'Line required',
                    value: this.$options.filters.currency(this.lineRequired, this.currencyCode)
                },
                {
                    text: 'Retained limit',
                    value: this.$options.filters.currency(this.captiveLineRequired, this.currencyCode)
                },
                {
                    text: 'Reinsured limit',
                    value: this.$options.filters.currency(this.reinsuredLineRequired, this.currencyCode)
                }
            ]
        },
        linesDataList() {
            if (this.isCaptiveReinsurance) {
                return [
                    {
                        text: 'Line Required',
                        value: this.$options.filters.currency(this.reinsuredLineRequired, this.currencyCode)
                    },
                    {
                        text: 'Line Written',
                        value: this.$options.filters.currency(this.reinsurerLineWritten, this.currencyCode)
                    },
                    {
                        text: 'Outstanding',
                        value: this.$options.filters.currency(this.outstanding, this.currencyCode)
                    }
                ]
            }

            return [
                {
                    text: 'Line Required',
                    value: this.$options.filters.currency(this.lineRequired, this.currencyCode)
                },
                {
                    text: 'Line Written',
                    value: this.$options.filters.currency(this.lineWritten, this.currencyCode)
                },
                {
                    text: 'Outstanding',
                    value: this.$options.filters.currency(this.outstanding, this.currencyCode)
                }
            ]
        },
        lineRequired: function () {
            if (this.isCaptiveReinsurance) {
                return parseFloat(this.declaration.current_contract_value);
            }
            return parseFloat(this.declaration.limit_of_liability);
        },
        captiveLineRequired: function () {
            return parseFloat(this.captiveDeclarationLine.line_amount);
        },
        reinsuredLineRequired: function () {
            return parseFloat((this.lineRequired - this.captiveDeclarationLine.line_amount));
        },
        lineWritten: function () {
            return (this.percentageWritten / 100) * this.declaration.limit_of_liability;
        },
        reinsurerLineWritten() {
            return (this.percentageWritten / 100) * this.reinsuredLineRequired;
        },
        isLineWrittenFull: function () {
            return this.percentageWritten === 100;
        },
        outstanding: function () {
            return this.lineRequired - this.lineWritten;
        },
        percentageWritten() {
            let currentLinePercentages = [];

            if (this.declaration.hasOwnProperty('lines')) {
                currentLinePercentages = this.allDeclarationLines.map(function (line) {
                    // check written percent is a valid number, otherwise interpret as zero
                    if (line.line_percent && !isNaN(line.line_percent)) {
                        return Number(parseFloat(line.line_percent).toFixed(12));
                    }

                    return 0;
                });
            }

            const line = [...currentLinePercentages].reduce((total, writtenLine) => total.plus(writtenLine), new Big(0));

            return parseFloat(line)
        },
        captivePercentageWritten() {
            return this.captiveDeclarationLine.line_percent;
        },
        percentageWrittenColor() {
            if (this.percentageWritten === 100) {
                return 'green';
            }

            if (this.percentageWritten > 100) {
                return 'red'
            }

            return 'yellow';
        },
        responseTypes() {
            const responseTypes = [
                {
                    text: 'Rate',
                    value: 'rate'
                }
            ];

            if (!this.isCaptiveReinsurance) {
                responseTypes.push({
                    text: 'Flat fee',
                    value: 'flat_fee'
                });
            }

            responseTypes.push(
                {
                    text: 'Percentage of net margin',
                    value: 'percentage_of_net_margin'
                },
                {
                    text: 'Percentage of gross margin',
                    value: 'percentage_of_gross_margin'
                },
            );

            return responseTypes;
        },
        insurerList() {
            const currentInsurers = this.declaration.lines?.map(
                line => (line.captive_insurer_company_id ? line.captive_insurer_company : line.insurer_company)
            ) ?? [];

            return [...currentInsurers, ...this.newLinesInsurers];
        },
        approvalList() {
            const currentInsurers = this.declaration.lines?.map(({insurer_company}) => ({
                ...insurer_company,
                is_new_insurer: false
            })) ?? [];

            const uniqueInsurers = new Map();
            [
                ...currentInsurers,
                ...this.newLinesInsurers.map(insurer => ({
                    ...insurer,
                    is_new_insurer: true
                })),
            ].forEach(insurer => {
                if (!uniqueInsurers.has(insurer.id)) {
                    uniqueInsurers.set(insurer.id, insurer);
                }
            });

            return [...uniqueInsurers.values()];
        },
        newLinesInsurers() {
            return this.newLines.filter(newLine => Boolean(newLine.insurer_company_id)).map(newLine => {
                return this.insurers.find(insurer => insurer.id === newLine.insurer_company_id);
            });
        },
        defaultBrokerage() {
            if (this.declaration.facility) {
                return this.declaration.facility.brokerage;
            }

            return 17.5;
        },
        isCaptiveReinsurance() {
            return this.declaration.transaction_type === 'captive_reinsurance';
        },
        captiveMaxRetainedAmount() {
            return 60000000.00;
        },
        hasValidDeclarationLines() {
            const validLines = this.declarationLines.every(line => {
                //check line type
                if (!line.offer_type) {
                    return false;
                }

                //check line price
                if ((this.isTypeRate(line.offer_type) && line.line_percent && !line.price) ||
                    (this.isTypeFlatFee(line.offer_type) && line.line_percent && !line.flat_fee) ||
                    (this.isTypePercentageOfMargin(line.offer_type) && line.line_percent && !line.percentage_of_margin)
                ) {
                    return false;
                }

                return true;
            });
            return this.isCaptiveReinsurance
                ? validLines && this.hasSameLineTypes
                : validLines && (this.pricingStructure === 'vertical' || (this.pricingStructure === 'horizontal' && this.hasSameLineTypes));
        },
        hasSameLineTypes() {
            return this.hasSameTypes(this.declarationLines);
        },
        hasPercentageOfGrossMarginLines() {
            return this.declarationLines.filter(line => line.offer_type === 'percentage_of_gross_margin').length > 0;
        },
        hasPercentageOfNetMarginLines() {
            return this.declarationLines.filter(line => line.offer_type === 'percentage_of_net_margin').length > 0;
        },
        percentageOfMarginOffersOnly() {
            return this.declarationLines.length === this.declarationLines.filter(o => ['percentage_of_net_margin', 'percentage_of_gross_margin'].includes(o.offer_type));
        },
        percentageOfNetMarginOffersOnly() {
            return this.declarationLines.length === this.declarationLines.filter(o => o.offer_type === 'percentage_of_net_margin').length;
        },
        percentageOfGrossMarginOffersOnly() {
            return this.declarationLines.length === this.declarationLines.filter(o => o.offer_type === 'percentage_of_gross_margin').length;
        },
        rateOffersOnly() {
            return this.declarationLines.length === this.declarationLines.filter(o => o.offer_type === 'rate').length;
        },
        isInvoice() {
            return this.declaration.quote_type?.key === 'invoice';
        },
        isStandalonePolicy() {
            return this.declaration.risk_structure === 'standalone_policy';
        },
        submitButtonLabel() {
            return this.isStandalonePolicy ? "Next" : 'Save changes';
        },
        documentableId() {
            if (this.amendment && this.amendment.length > 0) {
                return this.amendment.id;
            }
            return this.declaration.endorsement_id;
        },
        numberOfInsurersSelected() {
            if (this.approvalList.length === this.approvingInsurerIds.length) {
                return "All insurers";
            }
            return this.approvingInsurerIds.length === 1
                ? "1 insurer"
                : this.approvingInsurerIds.length + " insurers";
        }
    },
    watch: {
        'amendment'(newAmendment) {
            this.amendmentId = newAmendment.id;
        },
        'declaration': {
            'handler': function () {
                this.declarationHasChanged = true;
                if (this.declarationHasChanged === true && this.effectiveDateChanged === true) {
                    this.isEditDisabled = false;
                }
            },
            deep: true
        },
        'declarationLines': {
            'handler': function () {
                this.declarationLinesChanged = true;
                this.checkPricingStructure();
                this.resetInsurers();
            },
            deep: true
        },
        'effectiveDate'() {
            this.effectiveDateChanged = true;
            if (this.declarationHasChanged === true && this.effectiveDateChanged === true) {
                this.isEditDisabled = false;
            }
        },
        async percentageWritten(newPercentageWritten) {
            if (this.percentagesNeedFixing(newPercentageWritten)) {
                await this.$nextTick(); // Fixes issue with written_line_amount being null on some offers.
                this.fixPercentages([...this.allDeclarationLines]);
            }
        },
        'amendmentStep'(newStep) {
            window.scroll({
                top: 0,
                left: 0,
                behavior: 'smooth'
            });

            if (newStep === 3) {
                this.originalNetMargin = this.declaration.net_margin;
                this.originalGrossMargin = this.declaration.gross_margin;
                this.adjustLinePercentages();
            }
        },
        'customNetMargin': {
            'handler': function (margin) {
                this.declaration.net_margin = margin;
            },
            immediate: true
        },
        'customGrossMargin': {
            'handler': function (margin) {
                this.declaration.gross_margin = margin;
            },
            immediate: true
        },
        'netMarginRequired': {
            'handler': function (value) {
                if (!value) {
                    this.customNetMargin = null;
                    this.declaration.net_margin = this.originalNetMargin;
                }
            },
            immediate: true
        },
        'grossMarginRequired': {
            'handler': function (value) {
                if (!value) {
                    this.customGrossMargin = null;
                    this.declaration.gross_margin = this.originalGrossMargin;
                }
            },
            immediate: true
        },
    },
    created() {
        this.fetchData();
    },
    methods: {
        async fetchData() {
            const dataRequests = [];

            dataRequests.push(axios.post(this.$root.api(`broker/declaration/${this.$route.params.id}/amendment/draft`)).then(res => {
                this.declaration = res.data.data;
                this.originalNetMargin = this.declaration.net_margin;
                this.originalGrossMargin = this.declaration.gross_margin;
                this.pricingStructure = this.declaration.pricing_structure;
                this.oldDeclaration = JSON.parse(JSON.stringify(res.data.data));
                this.effectiveDate = this.declaration.draft_endorsement?.effective_date;
                this.endorsementReference = this.declaration.draft_endorsement?.reference;
                this.commentary =  this.declaration.draft_endorsement?.commentary;

                this.declaration.inception_date = this.$options.filters.formatDate(this.declaration.inception_date, undefined, 'YYYY-MM-DD');
                this.declaration.expiry_date = this.$options.filters.formatDate(this.declaration.expiry_date, undefined, 'YYYY-MM-DD');
            }));
            dataRequests.push(axios.get(this.$root.api(`facility`)).then(res => {
                this.facilities = res.data.data;
            }));
            dataRequests.push(axios.get(this.$root.api(`currency`)).then(res => {
                this.currencies = res.data.data;
            }));
            dataRequests.push(axios.get(this.$root.api('categories')).then(res => {
                this.categories = res.data.data
            }));
            dataRequests.push(axios.get(this.$root.api(`law-jurisdiction`)).then(res => {
                this.choices_of_law = res.data.data;
            }));
            dataRequests.push(axios.get(this.$root.api(`country`)).then(res => {
                this.countries = res.data.data;
            }));
            dataRequests.push(this.loadInsurerRoleOptions());
            dataRequests.push(axios.get(this.$root.api('product')).then(res => {
                this.products = res.data.data
            }));

            axios.all(dataRequests).then(() => {
                this.declarationHasChanged = false;
                this.declarationLinesChanged = false;
                this.loading = false;

                if (this.declaration.facility) {
                    this.insurers = this.declaration.facility.insurer_companies;
                } else {
                    axios.get(this.$root.api('insurer')).then(res => {
                        this.insurers = res.data.data;
                    });
                }

                const securityUrl = this.declaration.facility_id
                    ? `broker/security/list/${this.declaration.facility_id}`
                    : 'broker/security/list';
                axios.get(this.$root.api(securityUrl)).then(res => {
                    this.securities = res.data.data;
                });
            });
        },
        async checkIsReferenceUnique() {
            await axios.get(this.$root.api(`broker/amendment/check-reference`), {
                params: {
                    declaration_id: this.declaration.original_declaration_id,
                    entity_reference: this.endorsementReference,
                    current_endorsement_id: this.declaration.draft_endorsement.id
                }
            }).then(response => {
                this.endorsementReferenceExists = response.data.exists;
            });

            if (this.endorsementReferenceExists) {
                if (await this.$refs.notUniqueEndorsementReference.open() === false) {
                    return;
                }
                window.scroll({
                    top: 0,
                    left: 0,
                    behavior: 'smooth'
                });
            }
        },
        async nextStep() {
            if (this.$refs.productForm.validate()) {
                if (this.amendmentStep === 1) {
                    await this.checkIsReferenceUnique();
                    if (this.endorsementReferenceExists) {
                        return;
                    }
                }
                this.amendmentStep++;
            }
        },
        // Check if percentage is very close to 100% so that we can attempt to update
        // the percentages to make the amendment go through.
        // TODO this logic is duplicated in FirmOrderBuilder.vue, could be abstracted
        percentagesNeedFixing(newPercentageWritten) {
            const autoFixRange = 0.001;

            return newPercentageWritten !== 100 &&
                newPercentageWritten > (100 - autoFixRange) &&
                newPercentageWritten < (100 + autoFixRange);
        },
        // Take the small amount of percent that's either under or over and attempt to add/subtract that between the
        // lines without changing the currency value.
        // TODO this logic is duplicated in FirmOrderBuilder.vue, could be abstracted
        fixPercentages(lines) {
            const limit = this.lineRequired;
            let remainingPercent = this.percentageRemaining(lines);

            for (let line of lines) {
                if (remainingPercent === 0) {
                    break;
                }

                const maxLinePercent = Math.min(line.line_percent, parseFloat(((line.line_amount / limit) * 100).toFixed(12)));
                let changeAmount = 0;
                if (remainingPercent < 0) {
                    changeAmount = Math.min(remainingPercent, maxLinePercent - line.line_percent);
                } else if (remainingPercent > 0) {
                    changeAmount = Math.max(remainingPercent, maxLinePercent - line.line_percent);
                }

                line.line_percent -= changeAmount;
                remainingPercent = this.percentageRemaining(lines);
            }
        },
        resetInsurers() {
            this.approvingInsurerIds = this.approvalList.filter((insurer) => {
                if (insurer.is_new_insurer) {
                    return true;
                }

                return this.declarationLines.some(({insurer_company_id, line_percent}) => insurer_company_id === insurer.id && line_percent > 0);
            }).map(insurer => insurer.id);
        },
        selectAllInsurers() {
            this.approvingInsurerIds = this.approvalList.map(insurer => insurer.id);
        },
        deselectAllInsurers() {
            this.approvingInsurerIds = this.approvalList.filter(({is_new_insurer}) => is_new_insurer).map(insurer => insurer.id);
        },
        percentageRemaining(lines) {
            const self = this;
            const percentageWritten = lines.map(function (line) {
                // check written percent is a valid number, otherwise interpret as zero
                if (line.line_percent && !isNaN(line.line_percent)) {
                    return Number(parseFloat(line.line_percent).toFixed(12));
                }

                return 0;
            }).reduce((total, writtenLine) => total + writtenLine, 0);

            return percentageWritten - 100;
        },
        async submit() {
            if (this.$refs.productForm.validate()) {
                if (!this.endorsementDocumentWarningDisplayed && this.documentCategoriesFromChild.some(doc => doc.text === 'Endorsement')) {
                    if (await this.$refs.noEndorsementDocument.open() === false) {
                        this.endorsementDocumentWarningDisplayed = true;
                        return;
                    }
                }

                if (await this.$refs.confirmAmendment.open() === false) {
                    this.resetInsurers();
                    return;
                }

                this.loading = true;

                this.declaration.new_lines = this.newLines;
                this.declaration.pricing_structure = this.pricingStructure;

                await axios.post(this.$root.api(`broker/declaration/${this.declaration.id}/amendment/submit`), {
                    endorsement_data: {
                        effective_date: this.effectiveDate,
                        commentary: this.commentary,
                        endorsement_id: this.declaration.draft_endorsement.id,
                        reference: this.endorsementReference,
                        approving_insurer_ids: this.approvingInsurerIds,
                    },
                    declaration: this.declaration,
                }).then(() => {
                    this.goToDeclaration();
                });
            }
        },
        goToDeclaration() {
            this.$router.push({path: `/agent/risk/${this.$route.params.id}`});
        },
        async adjustLinePercentages() {
            // if the limit of liability has increased, maintain line limits instead of line percentages
            if (
                (!this.declarationLinesChanged && (this.declaration.limit_of_liability > this.oldDeclaration.limit_of_liability)) ||
                (this.isCaptiveReinsurance && (this.declaration.limit_of_liability > this.oldDeclaration.limit_of_liability))
            ) {
                if (this.isCaptiveReinsurance) {
                    this.declaration.lines.forEach(line => {
                        line.line_percent = (line.line_amount * 100) / this.declaration.current_contract_value;
                    });
                } else {
                    // foreach line, calculate the adjusted percentage based on maintaining the line amount
                    this.declaration.lines.forEach(line => {
                        const originalLinePercent = this.oldDeclaration.lines.find(({id}) => id === line.id).line_percent;
                        line.line_percent = originalLinePercent * this.oldDeclaration.limit_of_liability / this.declaration.limit_of_liability;
                    });
                }

                await this.$nextTick(); // we wait for nextTick so that we can reset declarationLinesChanges to false
                this.declarationLinesChanged = false;
            }
        },
        addInsurer() {
            this.newLines.push({
                insurer_company_id: null,
                security_id: null,
                brokerage: this.defaultBrokerage,
                offer_type: null,
                price: null,
                flat_fee: null,
                percentage_of_margin: null,
                line_percent: null,
                line_amount: null,
                minimum_price: null,
                insurer_role: null,
            });
        },
        removeInsurer(index) {
            this.newLines.splice(index, 1);
        },
        async clearInsurerData(line) {
            if (await this.$refs.removeInsurer.open() === false || !line) {
                return;
            }

            const decIndex = this.declaration.lines.findIndex(dec => dec.id === line.id);

            if (decIndex === -1) {
                return;
            }

            this.declaration.lines[decIndex].brokerage = 0;
            this.declaration.lines[decIndex].price = 0;
            this.declaration.lines[decIndex].flat_fee = 0;
            this.declaration.lines[decIndex].percentage_of_margin = 0;
            this.declaration.lines[decIndex].line_amount = 0;
            this.declaration.lines[decIndex].line_percent = 0;
            this.declaration.lines[decIndex].minimum_price = 0;
        },
        getAvailableInsurers(newLine) {
            return this.insurers.filter(insurer => {
                if (insurer.id === newLine.insurer_company_id) {
                    return true;
                }

                const otherLines = [
                    ...this.declaration.lines.filter(line => line.insurer_company_id === insurer.id && line.line_percent > 0),
                    ...this.newLines.filter(line => line !== newLine && line.insurer_company_id === insurer.id),
                ];

                if (otherLines.length === 0) {
                    return true;
                }

                if (!otherLines.every(line => Boolean(line.security_id))) {
                    return false;
                }

                const usedSecurityIds = otherLines.map(line => line.security_id);
                const insurerSecurities = this.securities.filter(({insurer_company_id}) => insurer_company_id === insurer.id);

                return insurerSecurities.some(security => !usedSecurityIds.includes(security.id));
            });
        },
        getAvailableSecurities(line) {
            const usedSecurityIds = [
                ...this.declaration.lines.filter(existingLine => existingLine !== line &&
                    existingLine.insurer_company_id === line.insurer_company_id &&
                    existingLine.line_percent > 0),
                ...this.newLines.filter(otherNewLine => otherNewLine !== line &&
                    otherNewLine.insurer_company_id === line.insurer_company_id),
            ].map(otherLine => otherLine.security_id).filter(Boolean);

            return this.getInsurerSecurities(line).filter(security => !usedSecurityIds.includes(security.id));
        },
        getInsurerCompanyName(line) {
            if (line.captive_insurer_company_id) {
                return line.captive_insurer_company.name;
            }

            return line.insurer_company.name;
        },
        getInsurerSecurities(line) {
            return this.securities.filter(({insurer_company_id}) => insurer_company_id === line.insurer_company_id);
        },
        getSecuritiesFormatted: function (securities) {
            return securities.map(function (security) {
                return {
                    'text': security.name,
                    'value': security.id
                };
            });
        },
        isTypeRate(type) {
            return type === 'rate';
        },
        isTypeFlatFee(type) {
            return type === 'flat_fee';
        },
        isTypePercentageOfMargin(type) {
            return ['percentage_of_net_margin', 'percentage_of_gross_margin'].includes(type);
        },
        hasSameTypes(lines) {
            let lineType = null;

            return lines.every(line => {
                const lType = line.offer_type || line.type
                //check line's type
                if (lineType && lineType !== lType) {
                    return false
                }
                lineType = lType;

                return true;
            });
        },
        checkPricingStructure() {
            if (!this.isCaptiveReinsurance) {
                this.pricingStructure = this.declaration.pricing_structure;

                if (!this.hasSameLineTypes || this.declarationLines.filter(line => line.offer_type === 'flat_fee').length > 0) {
                    this.pricingStructure = 'vertical';
                }
            }
            this.calculateHorizontalPrice();
        },
        getPercentageOfMarginOffer(line) {
            if ((this.declaration.net_margin !== null && line.type === 'percentage_of_net_margin') ||
                (this.declaration.gross_margin !== null && line.type === 'percentage_of_gross_margin')) {
                return this.$options.filters.numberNoTrail(line.price) + ' BPS';
            } else if ((this.declaration.net_margin === null && line.type === 'percentage_of_net_margin') ||
                (this.declaration.gross_margin === null && line.type === 'percentage_of_gross_margin')) {
                return `${line.percentage_of_margin}%  (Min: ${line.minimum_price} BPS)`;
            }

        },
        calculateHorizontalPrice() {
            if (this.declarationLines.length === 0) {
                this.pricing.horizontalPercentageOfMargin = false;
                this.pricing.horizontalPrice = false;
            } else if (this.percentageOfNetMarginOffersOnly) {
                const margin = this.declaration.net_margin;
                const highestMinimumPrice = Math.max(
                    ...this.declarationLines
                        .filter(o => o.type === 'percentage_of_net_margin')
                        .map(o => o.minimum_price));
                const highestPercentageOfMargin = Math.max(
                    ...this.declarationLines
                        .filter(o => o.type === 'percentage_of_net_margin')
                        .map(o => o.percentage_of_margin));

                this.pricing.horizontalPercentageOfMargin = highestPercentageOfMargin;

                this.pricing.horizontalPrice = Math.max(margin * (highestPercentageOfMargin / 100), highestMinimumPrice).toFixed(2);
            } else if (this.percentageOfGrossMarginOffersOnly) {
                const margin = this.declaration.gross_margin;
                const highestMinimumPrice = Math.max(
                    ...this.declarationLines
                        .filter(o => o.type === 'percentage_of_gross_margin')
                        .map(o => o.minimum_price));
                const highestPercentageOfMargin = Math.max(
                    ...this.declarationLines
                        .filter(o => o.type === 'percentage_of_gross_margin')
                        .map(o => o.percentage_of_margin));

                this.pricing.horizontalPercentageOfMargin = highestPercentageOfMargin;

                this.pricing.horizontalPrice = Math.max(margin * (highestPercentageOfMargin / 100), highestMinimumPrice).toFixed(2);
            } else if (this.rateOffersOnly) {
                const highestPrice = Math.max(...this.declarationLines.map(o => o.price));

                this.pricing.horizontalPrice = highestPrice;
            }
        },
        showHorizontalPriceChange(originalPrice) {
            return this.rateOffersOnly && this.pricingStructure === 'horizontal' && this.pricing.horizontalPrice > originalPrice;
        },
        showHorizontalPercentageChange(line, isRetainedOffer) {
            if (this.netMarginRequired && this.marginNotProvided) {
                return false;
            }

            if (this.grossMarginRequired && this.marginNotProvided) {
                return false;
            }


            if (isRetainedOffer) {
                return this.percentageOfMarginOffersOnly && this.pricing.horizontalPercentageOfMargin;
            }

            return this.percentageOfMarginOffersOnly && this.pricingStructure === 'horizontal' && this.pricing.horizontalPrice > parseFloat(this.getPercentageOfMarginOffer(line));
        },
        getEndLabelColor(percentage) {
            if (percentage === 100) {
                return 'secondary';
            } else if (percentage > 100) {
                return 'error'
            }

            return 'amber';
        },
        handleInsurerRoleChange(line, value) {
            this.$set(line, 'insurer_role', value || null);
        },
        handleOfferTypeChange(line) {
            if (line.offer_type === 'rate') {
                this.$set(line, 'percentage_of_margin', 0);
                this.$set(line, 'minimum_price', 0);
                this.$set(line, 'flat_fee', 0);
            }

            if (['percentage_of_net_margin', 'percentage_of_gross_margin'].includes(line.offer_type)) {
                this.$set(line, 'flat_fee', 0);
            }

            if (line.offer_type === 'flat_fee') {
                this.$set(line, 'percentage_of_margin', 0);
                this.$set(line, 'minimum_price', 0);
                this.$set(line, 'price', 0);
            }
        },
        async saveChanges() {
            if (this.$refs.productForm.validate()) {
                await this.checkIsReferenceUnique();

                if (this.endorsementReferenceExists) {
                    return;
                }

                this.btnLoading = true;
                this.declaration.new_lines = this.newLines;
                this.declaration.pricing_structure = this.pricingStructure;
                await axios.post(this.$root.api(`broker/declaration/${this.declaration.original_declaration_id}/amendment/${this.declaration.id}/save-changes`), {
                    endorsement_data: {
                        effective_date: this.effectiveDate,
                        commentary: this.commentary,
                        endorsement_id: this.declaration.draft_endorsement.id,
                        reference: this.endorsementReference,
                    },
                    declaration: this.declaration,
                }).then(response => {
                    this.newLines = [];
                    this.declaration = response.data.data;
                    this.loading = false;
                    this.btnLoading = false;
                });
            }
        },
        documentChanges(documentChanges) {
            if (documentChanges.isProcessing) {
                this.submitBtnText = 'Processing';
                this.isButtonDisabled = true;
            } else {
                this.submitBtnText = this.isUpdating ? 'Update' : 'Submit';
                this.isButtonDisabled = false;
            }

            this.uploadedDocumentsData = documentChanges.selectedDocumentData;
        },
        handleDocumentCategories(categories) {
            this.documentCategoriesFromChild = categories;
        },
    }
}
</script>

<style lang="scss" scoped>
.captive-line-table {
    :deep(thead) tr th:last-child {
        display: none;
    }
}

.value-percent-amount {
    width: 67%;
}

.value-percent-divider {
    width: 10px;
    margin: 16px 3px 0 3px;
    color: $color-grey-5;
    line-height: 44px;

    &.no-margin {
        margin-top: 0;
    }

    &.small {
        line-height: 30px;
    }
}

.value-percent-percentage {
    width: 33%;
}

::v-deep .x-list--disabled {
    opacity: 0.35;
}
</style>
